import type { Role } from "@beacon/core/access"
import type { SignInProvider } from "@beacon/core/sign-in"
import { queryOptions } from "@tanstack/react-query"
import { emailOTPClient } from "better-auth/client/plugins"
import { createAuthClient } from "better-auth/react"
import { resolveApiBase, sameOriginPath, signInBaseUrl } from "./urls.ts"

/** Where the web API is, for every call the web app makes to it. */
export const apiBase = resolveApiBase(import.meta.env.VITE_API_URL, window.location.origin)

const client = createAuthClient({ baseURL: signInBaseUrl(apiBase), plugins: [emailOTPClient()] })

export type { SignInProvider }

/** The ways in that send the browser to someone else to sign in: Google and Slack. */
export type SocialProvider = Exclude<SignInProvider, "email-code" | "password">

/**
 * The ways this stage offers to sign in. Google is always one, so a stage whose answer can't be
 * read still offers it.
 */
export const signInProvidersQuery = queryOptions({
  queryKey: ["sign-in-providers"],
  queryFn: async (): Promise<readonly SignInProvider[]> => {
    const response = await fetch(`${apiBase}/sign-in/providers`)
    if (!response.ok) return ["google"]
    return ((await response.json()) as { providers: SignInProvider[] }).providers
  },
  staleTime: Infinity,
})

/**
 * Sends the browser to Google or Slack, and back again once it knows who they are: to `next`, the
 * path they were trying to open, when it is one of the web app's own, and to its root otherwise.
 */
export function signInWith(provider: SocialProvider, next?: string) {
  const { origin } = window.location
  const path = sameOriginPath(next, origin) ?? "/"
  return client.signIn.social({ provider, callbackURL: `${origin}${path}` })
}

/**
 * Signs in on the way to connecting an agent: `oauthQuery` is the signed query the authorization
 * server sent the browser to the sign-in page with. Once Google or Slack knows who they are, Beacon
 * carries on with the agent's request, to the consent page, rather than to any page of the web
 * app's.
 */
export function signInToConnectAgent(provider: SocialProvider, oauthQuery: string) {
  const { origin } = window.location
  return client.signIn.social({
    provider,
    callbackURL: `${origin}/`,
    // Read by the authorization server's own hook on sign-in; Better Auth's client sends any extra
    // field along in the body.
    ...{ oauth_query: oauthQuery },
  })
}

/**
 * Asks Beacon to email a sign-in code to `email`. It answers the same whoever the address belongs
 * to, and sends a code only when it is one of a Member's verified addresses.
 */
export function emailMeACode(email: string) {
  return client.emailOtp.sendVerificationOtp({ email: email.trim(), type: "sign-in" })
}

/**
 * Signs in with the code emailed to `email`. With `oauthQuery`, the signed query an agent's sign-in
 * arrived with, Beacon carries on with that agent's request and answers where to send the browser
 * next (`url`).
 */
export function signInWithCode(email: string, otp: string, oauthQuery?: string) {
  return client.signIn.emailOtp({
    email: email.trim(),
    otp,
    // Read by the authorization server's own hook on sign-in, as with Google and Slack.
    ...(oauthQuery !== undefined && { oauth_query: oauthQuery }),
  })
}

/**
 * Signs in with a password and a username or any of a Member's verified emails. A wrong password, a
 * locked account and someone Beacon doesn't know are all refused alike. With `oauthQuery`, Beacon
 * carries on with that agent's request, as with a code.
 */
export function signInWithPassword(typed: string, password: string, oauthQuery?: string) {
  return client.signIn.email({
    email: typed.trim(),
    password,
    ...(oauthQuery !== undefined && { oauth_query: oauthQuery }),
  })
}

/**
 * Asks Beacon to email a code that sets a new password, to the email typed or, for a username, the
 * Member's primary email. It answers the same whoever it belongs to.
 */
export function emailMeAResetCode(typed: string) {
  return client.emailOtp.requestPasswordReset({ email: typed.trim() })
}

/** Sets a new password with the code emailed for it; then the password signs in. */
export function resetPassword(typed: string, otp: string, password: string) {
  return client.emailOtp.resetPassword({ email: typed.trim(), otp, password })
}

/** What an invitation's page shows, for a token still good; `null` for anything else. */
export const invitationQuery = (token: string) =>
  queryOptions({
    queryKey: ["invitation", token],
    queryFn: async (): Promise<{ name: string; needsUsername: boolean } | null> => {
      const response = await fetch(`${apiBase}/invitations/look`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      })
      return response.ok
        ? ((await response.json()) as { name: string; needsUsername: boolean })
        : null
    },
    retry: false,
  })

/**
 * Sets an invited Member's password, and their username when they must choose one, answering what
 * they sign in with, or why it was refused.
 */
export async function acceptInvitation(
  token: string,
  choice: { password: string; username?: string },
): Promise<{ ok: true; signInAs: string } | { ok: false; refusal: string }> {
  const response = await fetch(`${apiBase}/invitations/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...choice }),
  })
  const answer = (await response.json().catch(() => ({}))) as { signInAs?: string; error?: string }
  return response.ok && answer.signInAs
    ? { ok: true, signInAs: answer.signInAs }
    : { ok: false, refusal: answer.error ?? "unknown" }
}

export function signOut() {
  return client.signOut()
}

/**
 * Who Beacon says you are: its own record of you, not Google's. `name` is how Beacon shows you, and
 * `firstName` your Profile's, for a greeting, or none until it is filled. `profileChecked` is
 * whether you have confirmed or skipped the check of your seeded Profile, which shows once.
 */
export type Me = {
  id: string
  name: string
  firstName: string | null
  email: string
  role: Role
  groups: { id: string; name: string }[]
  profileChecked: boolean
}

/**
 * You, or nobody (`null`) when you are not signed in. Nobody is an answer, not a failure: a query
 * may not resolve to `undefined`, so it is `null`, and only a call that never got an answer fails.
 */
export async function fetchMe(): Promise<Me | null> {
  // The session is a cookie on the web API's host, so it rides along only when asked for.
  const response = await fetch(`${apiBase}/me`, { credentials: "include" })
  return response.ok ? ((await response.json()) as Me) : null
}

/** You, as a query: the one every page's session is read from. */
export const meQuery = queryOptions({ queryKey: ["me"], queryFn: fetchMe })
